import React from 'react';
import { useResetOnKeyChange } from '@/utils';
import {
    moveHighlight,
    paletteRowElementId,
    searchActions,
    selectableResults,
    type ControlAction,
    type PaletteResolvers,
    type PaletteResult,
} from '../../controlCenterMeta';

type UseCommandPaletteArgs = {
    isOpen: boolean;
    /** Resolved by the caller: matching happens against the visible language. */
    resolvers: PaletteResolvers;
    onRun: (action: ControlAction) => void;
    onTogglePin: (id: string) => void;
    onClose: () => void;
};

export type UseCommandPaletteResult = {
    query: string;
    onQueryChange: (next: string) => void;
    results: PaletteResult[];
    /** The rows a keyboard may land on, in list order. */
    selectable: ControlAction[];
    /** Index into `selectable`. Always valid, or 0 when there are no rows. */
    highlight: number;
    onHighlight: (index: number) => void;
    highlightedId: string | null;
    onKeyDown: (event: React.KeyboardEvent) => void;
};

/**
 * The palette's state: what has been typed, and where the keyboard is.
 *
 * **The keys live on the input, not on the list.** The caret never leaves the
 * field — that is what makes typing-then-choosing one gesture instead of two —
 * so arrows, Enter, Escape and the pin chord are handled where focus already
 * is, and the list follows. The one exception is `Ctrl/⌘ K` itself, which is a
 * page-wide accelerator and belongs on the window; the page owns it.
 *
 * **The highlight indexes the selectable rows only.** Coming-soon matches are
 * rendered so that a search never dead-ends silently, but they are not
 * reachable by arrow, by Enter, or by click — a keyboard that lands on rows
 * that do nothing is worse than one that skips them.
 */
export const useCommandPalette = ({
    isOpen,
    resolvers,
    onRun,
    onTogglePin,
    onClose,
}: UseCommandPaletteArgs): UseCommandPaletteResult => {
    const [query, setQuery] = React.useState('');
    const [highlight, setHighlight] = React.useState(0);

    // Each opening starts blank. A palette that reopens holding the last search
    // is one that answers a question nobody asked this time.
    useResetOnKeyChange(isOpen, () => {
        setQuery('');
        setHighlight(0);
    });

    const results = React.useMemo(
        () => searchActions(query, resolvers),
        [resolvers, query],
    );

    const selectable = React.useMemo(
        () => selectableResults(results),
        [results],
    );

    // Clamped against the list it indexes rather than reset on every keystroke:
    // narrowing a query while sitting on row three should land on the last row,
    // not jump silently back to the top mid-thought.
    const count = selectable.length;
    const safeHighlight = count === 0 ? 0 : Math.min(highlight, count - 1);
    const highlighted = selectable[safeHighlight] ?? null;

    const onQueryChange = React.useCallback((next: string) => {
        setQuery(next);
        setHighlight(0);
    }, []);

    // Keep the highlighted row on screen. Found by id rather than by a ref per
    // row: `aria-activedescendant` already addresses them this way.
    React.useEffect(() => {
        if (!highlighted) {
            return;
        }
        document
            .getElementById(paletteRowElementId(highlighted.id))
            ?.scrollIntoView({ block: 'nearest' });
    }, [highlighted]);

    const onKeyDown = React.useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                // Preventing default keeps the caret still: without it the
                // browser sends it to one end of the input on every press.
                event.preventDefault();
                setHighlight((current) =>
                    moveHighlight(
                        Math.min(current, Math.max(count - 1, 0)),
                        event.key === 'ArrowDown' ? 1 : -1,
                        count,
                    ),
                );
                return;
            }
            if (event.key === 'Enter') {
                event.preventDefault();
                if (highlighted) {
                    onRun(highlighted);
                }
                return;
            }
            // Shift-P pins the highlighted row without leaving the palette —
            // the second way the pinned grid gets curated, the catalog's own
            // per-row pin being the first.
            if (event.shiftKey && event.key.toLowerCase() === 'p') {
                if (!highlighted) {
                    return;
                }
                event.preventDefault();
                onTogglePin(highlighted.id);
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                // The first Escape clears a query; the second closes. Someone
                // mid-search usually means "not that search", not "not this".
                if (query.length > 0) {
                    onQueryChange('');
                    return;
                }
                onClose();
            }
        },
        [
            count,
            highlighted,
            onClose,
            onQueryChange,
            onRun,
            onTogglePin,
            query.length,
        ],
    );

    return {
        query,
        onQueryChange,
        results,
        selectable,
        highlight: safeHighlight,
        onHighlight: setHighlight,
        highlightedId: highlighted?.id ?? null,
        onKeyDown,
    };
};
